import type { DraftCompileStatus } from '@/hooks/useWriterProjectsOverview';

export interface DraftStatusChip {
  label: string;
  /** Live compiles get a subtle pulse; everything else stays calm. */
  live: boolean;
  tone: 'neutral' | 'attention';
}

/**
 * The chip vocabulary is bounded by data that actually exists —
 * `writer_versions.compile_status`, nothing invented.
 */
export function draftStatusChip(status: DraftCompileStatus): DraftStatusChip {
  switch (status) {
    case 'pending':
    case 'running':
      return { label: 'Compiling', live: true, tone: 'neutral' };
    case 'failed':
      return { label: 'Compile failed', live: false, tone: 'attention' };
    case 'success':
      return { label: 'Compiled', live: false, tone: 'neutral' };
    default:
      return { label: 'No compile yet', live: false, tone: 'neutral' };
  }
}

/** Short, humane "last touched" label. */
export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff)) return '';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return days === 1 ? 'yesterday' : `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'last month' : `${months} months ago`;
}
